import React, { useState } from 'react';
import './LandingPage.css';

const featureCards = [
  {
    icon: '📦',
    title: 'Raw materials to finished goods',
    description:
      'Track every input and every output in one place—no more blind spots between store and shop floor.',
  },
  {
    icon: '⚡',
    title: 'Live stock you can trust',
    description:
      'Always know what is available, what is on hold, and what needs re‑ordering today.',
  },
  {
    icon: '🧭',
    title: 'Owner‑level visibility',
    description:
      'See purchasing gaps, slow‑moving items, and stock health without chasing spreadsheets.',
  },
  {
    icon: '🔒',
    title: 'Role‑based control',
    description:
      'Admins manage structure. Operators focus on daily entries with safe permissions.',
  },
];

const faqCategories = [
  {
    title: 'Inventory & Materials',
    items: [
      {
        question: 'How do I record incoming material?',
        answer:
          'Add an entry whenever material is purchased or received. Stock is updated instantly, and each movement is recorded for reference.'
      },
      {
        question: 'Can I organize materials in a structured way?',
        answer:
          'Yes. Materials can be organized with categories and attributes to keep entries consistent and reporting more reliable.'
      },
      {
        question: 'Can I track stock by material type?',
        answer:
          'Yes. You can review stock and movement history by material type using filters for faster analysis.'
      },
      {
        question: 'Will the app help prevent stock mistakes?',
        answer:
          'Yes. Structured forms and clear movement logs help reduce entry errors and improve day-to-day stock control.'
      }
    ]
  },
  {
    title: 'Orders & Workflow',
    items: [
      {
        question: 'Can I track orders through different stages?',
        answer:
          'Yes. Orders can be tracked from creation to completion through defined workflow stages.'
      },
      {
        question: 'Are there multiple ways to view work progress?',
        answer:
          'Yes. Different views are available to support both stage-wise tracking and schedule-based planning.'
      },
      {
        question: 'Can products be connected to material requirements?',
        answer:
          'Yes. Products can include defined material requirements to support better planning and expected-usage visibility.'
      }
    ]
  },
  {
    title: 'Setup & Team Access',
    items: [
      {
        question: 'Can I customize forms and dropdown options later?',
        answer:
          'Yes. Forms and dropdown options can be updated as your process evolves.'
      },
      {
        question: 'Can different team members have different access levels?',
        answer:
          'Yes. Role-based permissions let each team member access only the areas relevant to their responsibilities.'
      },
      {
        question: 'Is the system suitable for daily operations?',
        answer:
          'Yes. The system is designed for daily operations, with clear records and practical workflows.'
      }
    ]
  }
];

function LandingPage() {
  const [openFaqKey, setOpenFaqKey] = useState(null);

  return (
    <div className="landing-page">
      <header className="landing-topbar">
        <div className="landing-brand">Inventory Control Studio</div>
        <div className="landing-topbar-actions">
          <a className="topbar-button" href="/login">
            Sign in
          </a>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <p className="hero-kicker">Built for manufacturing owners</p>
          <h1>Know your stock. Stop production delays. Run tighter operations.</h1>
          <p className="hero-subtitle">
            Inventory Control Studio gives business owners a real‑time view of raw materials,
            work‑in‑process, and finished goods—so you can buy right, produce on time, and
            keep margins healthy.
          </p>
          <div className="hero-actions">
            <a className="hero-primary" href="/login">
              Launch your dashboard
            </a>
          </div>
        </section>

        <section className="landing-features">
          <div className="section-heading">
            <h2>Made for the realities of manufacturing</h2>
            <p>
              Clear stock data, faster buying decisions, and fewer production surprises.
            </p>
          </div>

          <div className="feature-grid">
            {featureCards.map((feature) => (
              <article key={feature.title} className="feature-card">
                <div className="feature-icon" aria-hidden="true">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-faq">
          <div className="section-heading">
            <h2>Frequently asked questions</h2>
            <p>
              Quick answers about inventory, workflow, setup, and daily operations.
            </p>
          </div>

          <div className="faq-category-grid">
            {faqCategories.map((category) => (
              <article key={category.title} className="faq-category-card">
                <h3>{category.title}</h3>
                <div className="faq-list">
                  {category.items.map((item) => (
                    <div key={item.question} className="faq-item">
                      <button
                        type="button"
                        className="faq-question"
                        onClick={() =>
                          setOpenFaqKey((prev) => (prev === item.question ? null : item.question))
                        }
                        aria-expanded={openFaqKey === item.question}
                      >
                        <span>{item.question}</span>
                        <span className="faq-toggle" aria-hidden="true">
                          {openFaqKey === item.question ? '−' : '+'}
                        </span>
                      </button>
                      {openFaqKey === item.question && <p>{item.answer}</p>}
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

      </main>
    </div>
  );
}

export default LandingPage;
